import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DownloadIcon from '@mui/icons-material/Download';

// InsiTech International brand palette — docs/color_pallete_typography_guide.md
const BRAND = {
  navy: '#0047AB',
  offWhite: '#F8F8F8',
  coral: '#E99361',
  darkGray: '#424242',
};

// Turns a topic's ordered `steps` (show_tell_topics.py) plus the learner's
// prep_brief answers into a slide deck — one slide per step, title = step
// label, body = the learner's answer. Two export paths: an in-app carousel
// for rehearsing, and a real .pptx (see loadPptxGenJS below). Print CSS
// gives browser Print > Save as PDF as a free second export path with no
// extra dependency.

let pptxGenJsPromise = null;

// pptxgenjs (already a project dependency, used at build time for curriculum
// decks in frontend/public/slides/generator/) still contains a literal
// `import("node:fs")` inside a runtime-guarded Node-only branch of
// pres.writeFile — dead code in a browser, but react-scripts 5's webpack 5
// doesn't understand the "node:" URI scheme and fails the *entire* production
// build if this package is ever imported from src/, even dynamically. Load it
// as a vendored classic <script> tag instead (frontend/public/vendor/, a
// straight copy of node_modules/pptxgenjs/dist/pptxgen.min.js) so webpack
// never parses the file at all — the same way the curriculum-deck generator
// already uses this library entirely outside the webpack build.
function loadPptxGenJS() {
  if (window.PptxGenJS) return Promise.resolve(window.PptxGenJS);
  if (!pptxGenJsPromise) {
    pptxGenJsPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = '/vendor/pptxgenjs.min.js';
      script.onload = () => (window.PptxGenJS ? resolve(window.PptxGenJS) : reject(new Error('pptxgenjs failed to load')));
      script.onerror = () => reject(new Error('Could not load the presentation export library.'));
      document.head.appendChild(script);
    });
  }
  return pptxGenJsPromise;
}
export default function PresentationDeckViewer({ open, onClose, topicTitle, steps, brief }) {
  const [index, setIndex] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const slides = useMemo(
    () => (steps || []).map((step) => ({
      key: step.key,
      title: step.label,
      body: String((brief || {})[step.key] || '').trim(),
    })),
    [steps, brief]
  );

  useEffect(() => {
    if (open) {
      setIndex(0);
      setExportError('');
    }
  }, [open]);

  if (!open) return null;

  const current = slides[index] || { title: '', body: '' };
  const filledCount = slides.filter((s) => s.body).length;

  const handleDownloadPptx = async () => {
    setExporting(true);
    setExportError('');
    try {
      const PptxGenJS = await loadPptxGenJS();
      const pres = new PptxGenJS();
      pres.author = 'Qode Clarity';
      pres.title = topicTitle;

      const titleSlide = pres.addSlide();
      titleSlide.background = { color: BRAND.navy.replace('#', '') };
      titleSlide.addText(topicTitle, {
        x: 0.5, y: 2.1, w: 9, h: 1.5, fontSize: 36, bold: true, color: 'FFFFFF', align: 'center',
      });
      titleSlide.addText('Qode Clarity Show & Tell', {
        x: 0.5, y: 3.6, w: 9, h: 0.5, fontSize: 16, color: BRAND.coral.replace('#', ''), align: 'center',
      });

      slides.forEach((slide) => {
        const s = pres.addSlide();
        s.background = { color: BRAND.offWhite.replace('#', '') };
        s.addText(slide.title, {
          x: 0.6, y: 0.5, w: 8.8, h: 0.8, fontSize: 26, bold: true, color: BRAND.navy.replace('#', ''),
        });
        s.addShape(pres.shapes.LINE, {
          x: 0.6, y: 1.28, w: 1.2, h: 0, line: { color: BRAND.coral.replace('#', ''), width: 3 },
        });
        s.addText(slide.body || '(not written yet)', {
          x: 0.6, y: 1.6, w: 8.8, h: 4.6, fontSize: 18, color: BRAND.darkGray.replace('#', ''), valign: 'top',
        });
      });

      const fileName = `${(topicTitle || 'show-and-tell').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.pptx`;
      await pres.writeFile({ fileName });
    } catch (err) {
      setExportError(err.message || 'Could not export the deck. Try the in-app viewer or print to PDF instead.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <style>{`
        .stf-print-only { display: none; }
        @media print {
          .stf-screen-only { display: none !important; }
          .stf-print-only { display: block !important; }
          .stf-print-slide { page-break-after: always; padding: 48px 40px; }
          .MuiBackdrop-root { display: none !important; }
          .MuiDialog-container, .MuiDialog-paper { position: static !important; overflow: visible !important; max-height: none !important; box-shadow: none !important; }
        }
      `}</style>
      <DialogTitle>{topicTitle} — deck preview</DialogTitle>
      <DialogContent>
        {exportError && <Alert severity="error" sx={{ mb: 2 }}>{exportError}</Alert>}
        {filledCount === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Fill in your prep brief to see it turn into slides here.
          </Alert>
        )}

        <Box className="stf-screen-only">
          <Box
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              minHeight: 320,
            }}
          >
            <Box sx={{ bgcolor: BRAND.navy, px: 3, py: 2 }}>
              <Typography variant="overline" sx={{ color: BRAND.coral, letterSpacing: 1.5 }}>
                Slide {index + 1} of {slides.length}
              </Typography>
              <Typography variant="h5" fontWeight={800} sx={{ color: '#fff' }}>
                {current.title}
              </Typography>
            </Box>
            <Box sx={{ bgcolor: BRAND.offWhite, px: 3, py: 3, minHeight: 220 }}>
              <Box sx={{ width: 48, height: 3, bgcolor: BRAND.coral, mb: 2 }} />
              <Typography variant="body1" sx={{ color: BRAND.darkGray, whiteSpace: 'pre-wrap' }}>
                {current.body || '(not written yet)'}
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5 }}>
            <IconButton onClick={() => setIndex((i) => Math.max(0, i - 1))} disabled={index === 0} size="small">
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption" color="text.secondary">
              {filledCount} of {slides.length} slides written
            </Typography>
            <IconButton onClick={() => setIndex((i) => Math.min(slides.length - 1, i + 1))} disabled={index >= slides.length - 1} size="small">
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        <Box className="stf-print-only">
          {slides.map((slide) => (
            <Box key={slide.key} className="stf-print-slide">
              <Typography variant="h4" fontWeight={800} sx={{ color: BRAND.navy, mb: 2 }}>{slide.title}</Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{slide.body || '(not written yet)'}</Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, ml: 1 }}>
          Tip: use your browser's Print → Save as PDF for a PDF copy.
        </Typography>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownloadPptx} disabled={exporting || filledCount === 0}>
          {exporting ? 'Exporting…' : 'Download .pptx'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
